import { CircularProgress } from "@mui/material";
import _ from "lodash";
import {
  ChangeEvent,
  FormEvent,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import Message, { MessageTypes } from "../Message/Message";
import {
  DesignFromDB,
  DesignState,
  ProductDefinition,
  Size,
} from "../../types";
import { Button, Dialog, Input } from "../../common/components";
import { Box, Typography } from "@mui/material";
import { createStyles } from "../../styles/theme";
import { useDesignFetcher } from "../../utils/DesignsFetcherHooks";
import { AdminOrderContext } from "../../context/Admin/Order";
import { AxiosError } from "axios";
import SizeInput from "../Sizes/SizeInput";
import { useLocation } from "react-router-dom";
import { AdminTechpackContext } from "../../context/Admin/Techpack";

const styles = createStyles({
  searchContainer: {
    display: "flex",
    gap: "16px",
    alignItems: "center",
    mb: "24px",
  },
  input: {
    flex: 1,
  },
  designPreview: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
    alignItems: "center",
  },
  designImages: {
    display: "flex",
    gap: "20px",
    "& img": {
      width: "100%",
      height: "100%",
      maxWidth: "138px",
      maxHeight: "192px",
      objectFit: "contain",
    },
  },
  designInfo: {
    flex: 1,
  },
  sizesGrid: {
    display: "grid",
    justifyContent: "center",
    gap: "8px",
    alignItems: "center",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    py: "40px",
  },
  button: {
    padding: "8px 12px",
    fontWeight: "normal",
  },
});

interface Props {
  open: boolean;
  onClose: (success: boolean) => void;
}

const AddDesignToPreorderModal = (props: Props) => {
  const { open, onClose } = props;
  const location = useLocation();
  const isAdmin = location.pathname.includes("/admin");
  const { orderDetail: preorderDetail, onAddNewDesign } =
    useContext(AdminOrderContext);
  const { t } = useTranslation();

  const [url, setUrl] = useState("");
  const [sizes, setSizes] = useState<Size[]>([]);
  const [designState, setDesignState] = useState<DesignState | null>(null);
  const [designFromBackend, setDesignFromBackend] =
    useState<DesignFromDB | null>(null);
  const [productDefinition, setProductDefinition] =
    useState<ProductDefinition | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isAdding, setisAdding] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const { fetchDesignAsAdmin } = useDesignFetcher();
  const { refetchTechpack } = useContext(AdminTechpackContext);

  const extractDesignId = useCallback((input: string) => {
    const uuidRegex =
      /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/gi;
    if (uuidRegex.test(input)) {
      return input;
    }

    // Try to extract UUID from studio route pattern: /teams/{teamId}/projects/{projectId}/designs/{designId}
    const studioRouteRegex =
      /\/teams\/[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}\/projects\/[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}\/designs\/([0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12})/i;
    const studioMatch = input.match(studioRouteRegex);
    if (studioMatch) {
      return studioMatch[1];
    }

    // Fallback to simple /designs/{designId} pattern
    const urlRegex =
      /\/designs\/([0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12})/i;
    const match = input.match(urlRegex);
    return match ? match[1] : null;
  }, []);

  const handleInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value || "";
    setUrl(input);
  }, []);

  const resetSearch = useCallback(() => {
    setUrl("");
    setSizes([]);
    setDesignState(null);
    setDesignFromBackend(null);
    setProductDefinition(null);
    setIsSearching(false);
    setisAdding(false);
    setError(undefined);
  }, [
    setUrl,
    setSizes,
    setDesignState,
    setDesignFromBackend,
    setProductDefinition,
    setIsSearching,
    setisAdding,
    setError,
  ]);

  const onSizeChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, size: Size) => {
      setSizes((oldSizes) => {
        const newSizes = _.cloneDeep(oldSizes);

        const sizeToUpdate = newSizes.find((s: Size) => s.name === size.name);
        if (sizeToUpdate) {
          sizeToUpdate.qty = size.qty;
        }

        return newSizes;
      });
    },
    [setSizes]
  );

  const onClickSearch = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      setIsSearching(true);

      const designId = extractDesignId(url);
      if (!designId) {
        setError(t("order.add_design_to_preorder.errors.invalid_input"));
        setIsSearching(false);
        return;
      }
      try {
        const designInformation = await fetchDesignAsAdmin(
          designId,
          preorderDetail?.user?.id ?? ""
        );
        setError(undefined);
        setDesignFromBackend(designInformation?.design);
        setDesignState(designInformation?.designState);
        setProductDefinition(
          designInformation?.productDefinition?.productDefinition
        );
        setSizes(
          designInformation.productDefinition.productDefinition.sizes.map(
            (size: Size) => {
              return { name: size.name, qty: 0 };
            }
          )
        );
      } catch (error: any) {
        if (error instanceof AxiosError) {
          if (error.status === 404) {
            setError(t("order.add_design_to_preorder.errors.404"));
          } else if (error.status === 403) {
            setError(t("order.add_design_to_preorder.errors.403"));
          } else {
            setError(t("errors.general"));
          }
        }
      } finally {
        setIsSearching(false);
      }
    },
    [extractDesignId, url, fetchDesignAsAdmin, preorderDetail?.user?.id, t]
  );

  const handleClose = useCallback(
    (success: boolean) => {
      resetSearch();
      onClose(success);
    },
    [resetSearch, onClose]
  );

  const onClickAdd = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      let isSuccess = false;
      setisAdding(true);
      try {
        const designId = extractDesignId(url) || "";
        await onAddNewDesign?.(designId, sizes);
        refetchTechpack();
        isSuccess = true;
      } catch (e: any) {
        isSuccess = false;
      } finally {
        setisAdding(false);
        handleClose(isSuccess);
      }
    },
    [
      url,
      sizes,
      onAddNewDesign,
      setisAdding,
      extractDesignId,
      handleClose,
      refetchTechpack,
    ]
  );

  const searchDisabled = useMemo(() => {
    if (!url) {
      return true;
    }

    const designId = extractDesignId(url);
    return !designId;
  }, [url, extractDesignId]);

  const availableSizes = useMemo(() => {
    if (!productDefinition) {
      return [];
    }
    return productDefinition.sizes;
  }, [productDefinition]);

  const addDesignDisabled = useMemo(() => {
    if (isAdding) {
      return true;
    }

    return false;
  }, [isAdding]);

  const content = (
    <Box>
      <Box
        component="form"
        onSubmit={onClickSearch}
        sx={styles.searchContainer}
      >
        <Input
          placeholder={t("order.add_design_to_preorder.search_placeholder")}
          value={url}
          onChange={handleInputChange}
          sx={styles.input}
          noMaxWidth
        />
        <Button
          variant="contained"
          type="submit"
          disabled={searchDisabled}
          sx={styles.button}
        >
          {t("general.search")}
        </Button>
      </Box>

      {error && (
        <Message
          title={error}
          type={MessageTypes.ALERT}
          fullWidth={true}
          titleIcon="warning"
        />
      )}

      {isSearching ? (
        <Box sx={styles.loadingContainer}>
          <CircularProgress />
        </Box>
      ) : designState ? (
        <Box sx={styles.designPreview}>
          <Typography variant="textLargeBold">
            {designFromBackend?.name}
          </Typography>
          <Box sx={styles.designImages}>
            {designState.images?.map((image) => (
              <img src={image.url} alt={image.side} key={image.side} />
            ))}
          </Box>

          {availableSizes.length > 0 && (
            <Box
              sx={{
                ...styles.sizesGrid,
                gridTemplateColumns: `repeat(${availableSizes.length}, 1fr)`,
              }}
            >
              {availableSizes.map((availableSize) => {
                const currentSize = sizes.find(
                  (size) => size.name === availableSize.name
                );
                return (
                  <SizeInput
                    key={availableSize.name}
                    itemSize={{
                      name: availableSize.name,
                      qty: currentSize?.qty || 0,
                    }}
                    onSizeChange={onSizeChange}
                    sx={{
                      gap: "8px",
                    }}
                  />
                );
              })}
            </Box>
          )}
          {designState && (
            <Button
              variant="contained"
              onClick={(e) => onClickAdd(e as any)}
              disabled={addDesignDisabled}
              sx={styles.button}
            >
              {t("order.add_design_to_preorder.add_to_order")}
            </Button>
          )}
        </Box>
      ) : null}
    </Box>
  );

  if (!isAdmin) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={() => handleClose(false)}
      title={t("order.add_design_to_preorder.title")}
      content={content}
    />
  );
};

export default AddDesignToPreorderModal;
